import React, { useState, useEffect } from "react";
import { Modal, Button, Upload, Typography, Steps, Progress, Input, Table, Alert, Spin, message } from "antd";
import {
  InboxOutlined, PlusOutlined, CheckCircleTwoTone, CloseCircleTwoTone,
  DownloadOutlined, EditOutlined, CloudUploadOutlined, ExportOutlined,
  FileTextOutlined, FlagOutlined, ArrowLeftOutlined, ArrowRightOutlined,
  CloseOutlined, FileDoneOutlined,
} from "@ant-design/icons";
import ReviewStep, { cellKey } from "./ReviewStep";
import ValidationPanel from "./ValidationPanel";
import BulkFileDrop from "./BulkFileDrop";
import matchFilesToRows, { REPORT_FIELD, METADATA_FIELD } from "./matchFilesToRows";
import useHistoricalOrderMasterData from "./useHistoricalOrderMasterData";
import buildHistoricalOrderFormData from "./buildHistoricalOrderFormData";
import { createHistoricalOrder, validateHistoricalOrderRows } from "../../../../../../../services/apis";
import {
  PARSE_DELAY_MS, fileErrorsForRow, csvStringToRows, rowsToCsvString,
} from "./mockData";

const { Dragger } = Upload;
const { Text, Title } = Typography;
const { Step } = Steps;

const STEP = Object.freeze({ INSTRUCTIONS: 0, UPLOAD: 1, REVIEW: 2, RESULT: 3 });

// Keeps every step's content area the same height so the modal doesn't jump
// in size when moving between a short step (Upload) and a long one (Review).
const BODY_MIN_HEIGHT = 460;

// Same teal used app-wide as @primary-color (see fe/craco.config.js) — kept as
// one consistent tint rather than a different color per step, matching how
// the rest of the app uses a single brand color rather than a rainbow.
const THEME_PRIMARY = "#0a8080";
const THEME_PRIMARY_TINT = "#e6f4f4";

// The real "Completed Orders Template" master spreadsheet's file id. Opening
// its /copy URL triggers Google's own "Make a copy" dialog (including the
// bound Apps Script) client-side — no Drive/Sheets API integration needed,
// same as how any other "copy this template" button works.
// The backend selects the Google Sheets template using its DD_ENV configuration.

const GUIDE_STEPS = [
  { icon: <DownloadOutlined />, label: "Step 1", title: "Copy Template", description: "Copy the Completed Orders template in Google Sheets" },
  { icon: <EditOutlined />, label: "Step 2", title: "Add details", description: "Add order details in the template — dropdowns narrow automatically as you pick Company, Project Code and Commodity" },
  { icon: <CloudUploadOutlined />, label: "Step 3", title: "Upload", description: "Export the sheet as CSV and upload it here" },
];

const IMPORTANT_NOTES = [
  "Use the provided template to enter completed order details.",
  "Don't add new columns or edit column headers.",
  "Only CSV files will be accepted.",
  "Company, Project Code, Agency, Commodity, Package, Sample Variant, Assigned Lab, Assigned Logistics and Target End Status are resolved against real reference data — fix any row the Review step flags before submitting.",
  "A real Report and Metadata file must be attached to every row in the Review step — drop the whole folder there and they're matched to rows automatically.",
  "For that matching to work, either fill the Report File Name / Report Metadata File Name columns, or name each file after the row's Sample Registration Number, QR Code Id, Sample Seal No or Batch ID.",
  "Leave Target End Status as \"Report Uploaded\" unless the order should be marked Completed.",
];

/**
 * "Bulk Completed Order Creation" — real bulk onboarding of historical orders
 * for any company, from a CSV export of the Historical Orders Google Sheet.
 * Company/Project/Agency/Commodity/Package/Lab/Logistics are resolved against
 * real data from GET /v1/historical-orders/master-data, and Submit really
 * calls POST /v1/historical-orders/create once per valid row — the same real
 * order-creation path AddCompletedOrder uses. No simulation.
 *
 * "Copy Template in Google Sheets" opens the real master template's /copy
 * URL supplied by the backend — Google's own "Make a copy" dialog, no Drive API
 * integration needed. Everything from there, including the CSV upload, is
 * real.
 */
export default function HistoricalOrderBulkUpload({ onBulkSuccess, onVisibilityChange, triggerLabel = "Bulk Completed Order Creation" }) {
  const { data: masterData, loading: masterDataLoading, error: masterDataError, refresh: refreshMasterData } = useHistoricalOrderMasterData();

  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(STEP.INSTRUCTIONS);
  // Tracks how far the user has actually progressed, separate from `step`
  // itself, so the stepper can be clicked to go back to an earlier step (e.g.
  // back to Instructions to re-open the template link) without also letting
  // it be clicked forward past a step whose validation hasn't passed yet.
  const [maxStepReached, setMaxStepReached] = useState(STEP.INSTRUCTIONS);
  const [parsing, setParsing] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState([]);
  // The last server verdict, and which cells have been edited since it — an
  // edit deliberately doesn't clear its own error, because whether the fix
  // actually holds is the server's call, not a guess made here.
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [dirtyCells, setDirtyCells] = useState(() => new Set());
  // What the last folder drop matched, kept so the unmatched files stay
  // listed by name instead of vanishing with the toast that announced them.
  const [fileMatch, setFileMatch] = useState(null);
  // All uploaded PDF files in the current session pool for quick assignment
  const [uploadedPdfs, setUploadedPdfs] = useState([]);
  // Which cell an error in the list last pointed at, so the grid can scroll
  // to it and ring it briefly.
  const [focusedCell, setFocusedCell] = useState(null);

  const goToStep = (next) => {
    setStep(next);
    setMaxStepReached((prev) => Math.max(prev, next));
  };

  const handleStepsClick = (next) => {
    if (next <= maxStepReached) setStep(next);
  };

  const reset = () => {
    setStep(STEP.INSTRUCTIONS);
    setMaxStepReached(STEP.INSTRUCTIONS);
    setParsing(false);
    setUploadedFileName(null);
    setRows([]);
    setEmail("");
    setSubmitting(false);
    setProgress({ done: 0, total: 0 });
    setResults([]);
    setValidation(null);
    setValidating(false);
    setDirtyCells(new Set());
    setFocusedCell(null);
    setFileMatch(null);
    setUploadedPdfs([]);
  };

  // A CSV has been parsed into rows or files dropped but the run hasn't finished — closing now
  // (by any means) would silently throw that away.
  const hasUnsavedData = (rows.length > 0 || step > STEP.INSTRUCTIONS || Boolean(uploadedFileName)) && !(step === STEP.RESULT && !submitting);

  const closeAndReset = () => {
    setVisible(false);
    onVisibilityChange?.(false);
    reset();
  };

  // Guards the modal's own close (X icon, Esc key, click outside — all of
  // which antd routes through onCancel) with a confirmation once there's
  // uploaded data still in progress.
  const confirmCloseIfNeeded = () => {
    if (!hasUnsavedData) {
      closeAndReset();
      return;
    }
    Modal.confirm({
      title: "Discard this import?",
      content: "Your uploaded sheet data will be cleared and this can't be undone.",
      okText: "Discard",
      okButtonProps: { danger: true },
      cancelText: "Keep editing",
      onOk: closeAndReset,
    });
  };

  // Browser-level actions (reload, close tab) always require confirmation
  // while the bulk modal is open.
  useEffect(() => {
    if (!visible) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [visible]);

  // Prevent a horizontal two-finger trackpad gesture from becoming browser
  // Back/Forward navigation. Normal vertical and in-modal scrolling still work.
  useEffect(() => {
    if (!visible) return undefined;
    const root = document.documentElement;
    const body = document.body;
    const previousRootValue = root.style.overscrollBehaviorX;
    const previousBodyValue = body.style.overscrollBehaviorX;
    root.style.overscrollBehaviorX = "none";
    body.style.overscrollBehaviorX = "none";
    return () => {
      root.style.overscrollBehaviorX = previousRootValue;
      body.style.overscrollBehaviorX = previousBodyValue;
    };
  }, [visible]);

  // Explicit browser Back/Forward navigation is allowed after confirmation.
  useEffect(() => {
    if (!visible) return undefined;
    window.history.pushState({ modalOpen: true }, "");
    let confirmationOpen = false;
    let restoringGuardEntry = false;
    const handlePopState = () => {
      if (restoringGuardEntry) {
        restoringGuardEntry = false;
        return;
      }
      if (confirmationOpen) return;
      confirmationOpen = true;
      Modal.confirm({
        title: "Leave bulk order creation?",
        content: "Your current import progress will be cleared if you leave this page.",
        okText: "Leave",
        okButtonProps: { danger: true },
        cancelText: "Stay",
        onOk: () => {
          window.removeEventListener("popstate", handlePopState);
          closeAndReset();
          window.history.back();
        },
        onCancel: () => {
          confirmationOpen = false;
          restoringGuardEntry = true;
          window.history.forward();
        },
      });
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [visible]);


  const handleFileDropped = (file) => {
    setParsing(true);
    setRows([]);
    setUploadedFileName(null);
    setValidation(null);
    setDirtyCells(new Set());
    setFileMatch(null);
    setUploadedPdfs([]);
    const reader = new FileReader();
    reader.onload = () => {
      // Kept as a short artificial delay so the "Parsing…" state is visible —
      // the parse itself (csvStringToRows) is real, not simulated.
      setTimeout(() => {
        try {
          const parsedRows = csvStringToRows(String(reader.result));
          if (!parsedRows.length) throw new Error("No data rows found below the header.");
          setRows(parsedRows);
          setUploadedFileName(file.name);
          setParsing(false);
        } catch (err) {
          setParsing(false);
          message.error(`Could not read this CSV: ${err.message}`);
        }
      }, PARSE_DELAY_MS);
    };
    reader.onerror = () => {
      setParsing(false);
      message.error("Could not read the selected file.");
    };
    reader.readAsText(file);
    return false;
  };

  // Lets the user back out of a parsed file without re-opening the browse
  // dialog on top of the "already uploaded" card — clears back to the empty
  // dropzone state.
  const handleRemoveFile = () => {
    setRows([]);
    setUploadedFileName(null);
    setValidation(null);
    setDirtyCells(new Set());
    setFileMatch(null);
    setUploadedPdfs([]);
  };

  // Marks the edited cells so the grid and the panel can both show that this
  // row's last verdict is now out of date. One edit can touch more than the
  // cell that was clicked — changing a parent clears the children scoped
  // under it, and moving a date clears later ones it strands — and those
  // cells need re-checking just as much as the one that was typed in.
  const handleChangeRows = (nextRows, changed) => {
    setRows(nextRows);
    if (!changed) return;
    // One cell edit passes a single { rowId, fields }; a bulk file drop passes
    // one per row it touched. Both mark the same way.
    const edits = Array.isArray(changed) ? changed : [changed];
    if (!edits.length) return;
    setDirtyCells((prev) => {
      const next = new Set(prev);
      edits.forEach((edit) => {
        (edit.fields || [edit.field]).forEach((field) => next.add(cellKey(edit.rowId, field)));
      });
      return next;
    });
  };

  /**
   * A dropped folder or set of PDF files, matched onto rows in one pass.
   */
  const handleAttachFiles = (fileList, ignoredNonPdfCount = 0, zeroByteCount = 0) => {
    const files = Array.from(fileList || []);
    if (!files.length) {
      if (zeroByteCount > 0) {
        message.error(`Rejected ${zeroByteCount} file(s) with size 0 bytes.`);
      } else if (ignoredNonPdfCount > 0) {
        message.warning(`Dropped ${ignoredNonPdfCount} file(s), but none were PDF. Only .pdf files are accepted.`);
      }
      return;
    }
    const result = matchFilesToRows(files, rows);
    setFileMatch({ ...result.summary, zeroByteCount: (result.summary?.zeroByteCount || 0) + zeroByteCount });
    setUploadedPdfs((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const newPdfs = (result.pdfFiles || []).filter((f) => !existingNames.has(f.name) && (f.size === undefined || f.size > 0));
      return [...prev, ...newPdfs];
    });
    handleChangeRows(result.rows, result.touched);
    const attached = result.summary.reports + result.summary.metadata;
    if (result.pdfFiles.length === 0) {
      if (zeroByteCount > 0) {
        message.error(`Rejected ${zeroByteCount} empty file(s) (0 bytes). No valid PDF files found.`);
      } else {
        message.warning("No PDF files found. Only .pdf files are accepted.");
      }
      return;
    }
    if (!attached) {
      message.info(
        `${result.pdfFiles.length} PDF file${result.pdfFiles.length === 1 ? "" : "s"} uploaded. You can assign them to order rows in the list below.`
      );
      return;
    }
    message.success(
      `Attached ${attached} file${attached === 1 ? "" : "s"} across ${result.touched.length} row${result.touched.length === 1 ? "" : "s"}.`
    );
  };

  const handleAssignFile = (file, targetRowId, targetField) => {
    if (file && file.size === 0) {
      message.error(`Cannot assign "${file.name}": file is empty (0 bytes).`);
      return;
    }
    setUploadedPdfs((prev) => {
      if (prev.some((f) => f.name === file.name)) return prev;
      return [...prev, file];
    });
    const nextRows = rows.map((row) => {
      if (row.rowId === targetRowId) {
        return { ...row, [targetField]: file };
      }
      return row;
    });
    handleChangeRows(nextRows, { rowId: targetRowId, fields: [targetField] });
  };

  const handleDetachFile = (targetRowId, targetField) => {
    const nextRows = rows.map((row) => {
      if (row.rowId === targetRowId) {
        return { ...row, [targetField]: null };
      }
      return row;
    });
    handleChangeRows(nextRows, { rowId: targetRowId, fields: [targetField] });
  };

  // The escape hatch for a drop that matched the wrong way round — clears the
  // attachments without touching any of the data in the rows.
  const handleDetachAll = () => {
    const touched = [];
    const nextRows = rows.map((row) => {
      const fields = [REPORT_FIELD, METADATA_FIELD].filter((field) => row[field]);
      if (!fields.length) return row;
      touched.push({ rowId: row.rowId, fields });
      return { ...row, [REPORT_FIELD]: null, [METADATA_FIELD]: null };
    });
    if (!touched.length) return;
    setFileMatch(null);
    handleChangeRows(nextRows, touched);
    message.info(`Detached files from ${touched.length} row${touched.length === 1 ? "" : "s"}.`);
  };

  // Remove a single PDF from the uploaded pool and detach if assigned anywhere
  const handleRemovePdfFile = (fileName) => {
    setUploadedPdfs((prev) => prev.filter((f) => f.name !== fileName));
    const touched = [];
    const nextRows = rows.map((row) => {
      let modified = false;
      const nextRow = { ...row };
      [REPORT_FIELD, METADATA_FIELD].forEach((field) => {
        if (nextRow[field] && nextRow[field].name === fileName) {
          nextRow[field] = null;
          touched.push({ rowId: row.rowId, fields: [field] });
          modified = true;
        }
      });
      return modified ? nextRow : row;
    });
    if (touched.length) {
      handleChangeRows(nextRows, touched);
    }
    message.info(`Removed "${fileName}" from PDF files.`);
  };

  // Clear all uploaded PDFs and detach all from rows
  const handleClearAllPdfFiles = () => {
    setUploadedPdfs([]);
    setFileMatch(null);
    const touched = [];
    const nextRows = rows.map((row) => {
      const fields = [REPORT_FIELD, METADATA_FIELD].filter((field) => row[field]);
      if (!fields.length) return row;
      touched.push({ rowId: row.rowId, fields });
      return { ...row, [REPORT_FIELD]: null, [METADATA_FIELD]: null };
    });
    if (touched.length) {
      handleChangeRows(nextRows, touched);
    }
    message.info("Cleared all uploaded PDF files.");
  };

  /**
   * The server is the authority on whether a row is importable — it resolves
   * every name against live reference data and reports the exact row/column
   * at fault. The only thing checked here is the attached files, which
   * genuinely don't exist server-side until submit.
   */
  const runValidation = async (rowsToCheck = rows) => {
    if (!rowsToCheck.length) return null;
    setValidating(true);
    try {
      const response = await validateHistoricalOrderRows(rowsToCheck);
      const data = response?.data?.data;
      const merged = {
        ...data,
        rows: data.rows.map((result) => {
          const row = rowsToCheck.find((r) => r.rowId === result.rowId);
          const fileErrors = row ? fileErrorsForRow(row) : [];
          return { ...result, errors: [...result.errors, ...fileErrors] };
        }),
      };
      setValidation(merged);
      setDirtyCells(new Set());
      return merged;
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Validation failed";
      message.error(`Could not validate: ${msg}`);
      return null;
    } finally {
      setValidating(false);
    }
  };

  // Check as soon as the grid is first shown, so the reviewer lands on a
  // marked-up sheet rather than an unannotated one they have to prompt.
  useEffect(() => {
    if (step === STEP.REVIEW && rows.length && !validation && !validating) {
      runValidation(rows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, rows.length]);

  const errorsByCell = {};
  const rowStatus = {};
  if (validation) {
    validation.rows.forEach((result) => {
      const row = rows.find((r) => r.rowId === result.rowId) || {};
      const target = String(row.targetEndStatus || "").trim().toLowerCase();
      const hasDate = Boolean(String(row.completedDate || "").trim());

      // Filter out stale completedDate errors if the user corrected them
      const currentErrors = result.errors.filter((err) => {
        if (err.field === "completedDate") {
          if (target === "report uploaded" && !hasDate) return false;
          if (target === "completed" && hasDate) return false;
        }
        return true;
      });

      // Add dynamic real-time completedDate errors
      if (target === "report uploaded" && hasDate) {
        if (!currentErrors.some((e) => e.field === "completedDate")) {
          currentErrors.push({ field: "completedDate", label: "Completed Date", message: 'must be blank unless Target End Status is "Completed"' });
        }
      } else if (target === "completed" && !hasDate) {
        if (!currentErrors.some((e) => e.field === "completedDate")) {
          currentErrors.push({ field: "completedDate", label: "Completed Date", message: 'is required when Target End Status is "Completed"' });
        }
      }

      rowStatus[result.rowId] = { count: currentErrors.length };
      currentErrors.forEach((error) => {
        const key = cellKey(result.rowId, error.field);
        if (!errorsByCell[key]) errorsByCell[key] = [];
        errorsByCell[key].push(error);
      });
    });
  }

  const rowIndexById = rows.reduce((map, row, index) => {
    map[row.rowId] = index + 1;
    return map;
  }, {});

  // Re-set on every click even for the same cell, so clicking the same error
  // twice scrolls back to it rather than doing nothing; the ring clears on a
  // timer so it doesn't linger as a permanent third cell state.
  const handleFocusCell = (rowId, field) => {
    setFocusedCell({ rowId, field, at: Date.now() });
    setTimeout(() => {
      setFocusedCell((current) => (current && current.rowId === rowId && current.field === field ? null : current));
    }, 2500);
  };

  const errorCount = validation
    ? Object.values(rowStatus).reduce((sum, stat) => sum + (stat?.count || 0), 0)
    : null;
  const validatedClean = validation !== null && errorCount === 0 && dirtyCells.size === 0;
  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const isEmailValid = EMAIL_REGEX.test(String(email || "").trim());
  const canSubmit = rows.length > 0 && validatedClean && isEmailValid && !validating;

  // Once every row is clean, the sheet the user edited inline (fixing
  // whatever validation flagged) IS the corrected source of truth — this
  // lets them take that correction back out as a real file, e.g. to keep as
  // a record or to re-import later, rather than it only ever existing as
  // in-grid state that disappears when the modal closes.
  const handleDownloadCorrected = () => {
    const csv = rowsToCsvString(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const baseName = (uploadedFileName || "completed-orders").replace(/\.csv$/i, "");
    const link = document.createElement("a");
    link.href = url;
    link.download = `${baseName}-corrected.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const submitLabel = () => {
    if (!validation) return "Validate before submitting";
    if (errorCount > 0) return `Fix ${errorCount} error${errorCount > 1 ? "s" : ""} to continue`;
    if (dirtyCells.size > 0) return "Re-validate to continue";
    return `Submit ${rows.length} order(s)`;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    goToStep(STEP.RESULT);
    setProgress({ done: 0, total: rows.length });
    const rowResults = [];
    // The ids came back from the validation that just passed, so submit uses
    // exactly what the server itself resolved — no second, possibly
    // divergent, client-side resolution pass.
    const resolvedByRowId = new Map((validation?.rows || []).map((r) => [r.rowId, r.resolved || {}]));
    // Sequential, not parallel — keeps load on the create endpoint (and its
    // blob upload + parsing trigger per row) reasonable, and gives real
    // incremental progress instead of an all-or-nothing wait.
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const resolved = resolvedByRowId.get(row.rowId) || {};
      const payloadRow = {
        ...row,
        company: resolved.companyId,
        projectCode: resolved.projectCodeId,
        agency: resolved.agencyId,
        commodity: resolved.commodityId,
        package: resolved.packageId,
        assignedLab: resolved.assignedLabId,
        assignedLogistics: resolved.assignedLogisticsId,
        state: resolved.stateName,
        district: resolved.districtName,
      };
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await createHistoricalOrder(buildHistoricalOrderFormData(payloadRow));
        rowResults.push({ ...row, ok: true, orderId: response?.data?.data?.orderId });
      } catch (err) {
        const msg = err?.response?.data?.message || err.message || "Failed to create order";
        rowResults.push({ ...row, ok: false, error: msg });
      }
      setProgress({ done: i + 1, total: rows.length });
    }
    setResults(rowResults);
    setSubmitting(false);
    const failedCount = rowResults.filter((r) => !r.ok).length;
    if (failedCount) {
      message.warning(`${rowResults.length - failedCount} / ${rowResults.length} orders created — ${failedCount} failed.`);
    } else {
      message.success(`All ${rowResults.length} orders created successfully.`);
    }
    onBulkSuccess?.();
  };

  const resultColumns = [
    {
      title: "Order", key: "order",
      render: (_, row) => <Text>{row.company} — {row.commodity}{row.batchId ? ` (${row.batchId})` : ""}</Text>,
    },
    {
      title: "Result", key: "result",
      render: (_, row) => (row.ok
        ? <Text><CheckCircleTwoTone twoToneColor="#52C41A" /> {row.orderId}</Text>
        : <Text type="danger"><CloseCircleTwoTone twoToneColor="#ff4d4f" /> {row.error}</Text>),
    },
  ];

  return (
    <>
      <Button
        onClick={() => {
          setVisible(true);
          onVisibilityChange?.(true);
        }}
        icon={<PlusOutlined />}
        style={{ borderRadius: 6, padding: "0 14px", display: "flex", alignItems: "center", gap: 6 }}
      >
        {triggerLabel}
      </Button>

      <Modal
        visible={visible}
        onCancel={confirmCloseIfNeeded}
        maskClosable={false}
        keyboard={!hasUnsavedData}
        title={(
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.4 }}>{triggerLabel}</div>
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
              Onboard many completed orders at once from a CSV export
            </Text>
          </div>
        )}
        width="90vw"
        style={{ maxWidth: 1600, top: 24 }}
        footer={null}
        destroyOnClose
      >
        {masterDataError && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="Could not load company/project reference data"
            description={masterDataError}
            action={<Button size="small" onClick={refreshMasterData}>Retry</Button>}
          />
        )}

        <Steps size="small" current={step} onChange={handleStepsClick} style={{ marginBottom: 24 }}>
          <Step title="Get template" description="Copy the sheet" icon={<FileTextOutlined />} />
          <Step title="Upload sheet" description="Export as CSV" icon={<CloudUploadOutlined />} />
          <Step title="Review & fix" description="Resolve any errors" icon={<EditOutlined />} />
          <Step title="Result" description="Orders created" icon={<FlagOutlined />} />
        </Steps>

        {masterDataLoading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spin tip="Loading company, project and reference data…" />
          </div>
        ) : (
        <div style={{ minHeight: BODY_MIN_HEIGHT, display: "flex", flexDirection: "column" }}>
          {step === STEP.INSTRUCTIONS && (
            <>
              <div style={{ display: "flex", gap: 16, background: "#F7F9FC", border: "1px solid #E6E8EF", borderRadius: 8, padding: 20 }}>
                {GUIDE_STEPS.map((guide) => (
                  <div key={guide.label} style={{ flex: 1 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8, background: THEME_PRIMARY_TINT, color: THEME_PRIMARY,
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, marginBottom: 8,
                    }}>
                      {guide.icon}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{guide.label}</Text>
                    <div style={{ fontWeight: 600, fontSize: 15, margin: "2px 0 4px" }}>{guide.title}</div>
                    <Text type="secondary" style={{ fontSize: 13 }}>{guide.description}</Text>
                  </div>
                ))}
              </div>

              <Title level={5} style={{ marginTop: 24 }}>Important!</Title>
              <ul style={{ paddingLeft: 20, margin: 0 }}>
                {IMPORTANT_NOTES.map((note) => (
                  <li key={note} style={{ marginBottom: 6 }}><Text>{note}</Text></li>
                ))}
              </ul>

              <div style={{ flex: 1 }} />

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 28 }}>
                <Button
                  icon={<ExportOutlined />}
                  disabled={!masterData?.templateUrl}
                  title={!masterData?.templateUrl ? "The template link has not been configured for this environment." : undefined}
                  onClick={() => { if (masterData?.templateUrl) window.open(masterData.templateUrl, "_blank", "noopener,noreferrer"); }}
                >
                  Copy Template in Google Sheets
                </Button>
                <Button type="primary" onClick={() => goToStep(STEP.UPLOAD)}>
                  Continue <ArrowRightOutlined />
                </Button>
              </div>
            </>
          )}

          {step === STEP.UPLOAD && (
            <>
              {/* antd's own .ant-upload.ant-upload-drag CSS already sets width:100%;
                  height:100% — it just needs a parent with a DEFINITE (not flex-auto)
                  height for that percentage to resolve against. flex:1 on this wrapper
                  computes to a definite height once laid out, which a plain child div
                  (no display override) fills correctly on both axes. */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", maxWidth: 640, margin: "0 auto", width: "100%" }}>
                {uploadedFileName && !parsing ? (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 16, border: "1px solid #E6E8EF",
                    borderRadius: 8, padding: "20px 24px", background: "#F7F9FC",
                  }}
                  >
                    <div style={{
                      width: 44, height: 44, borderRadius: 8, background: THEME_PRIMARY_TINT, color: THEME_PRIMARY,
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0,
                    }}
                    >
                      <FileDoneOutlined />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text strong ellipsis style={{ display: "block" }}>{uploadedFileName}</Text>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        <CheckCircleTwoTone twoToneColor="#52C41A" /> Parsed {rows.length} row{rows.length === 1 ? "" : "s"}
                      </Text>
                    </div>
                    <Button size="small" icon={<CloseOutlined />} onClick={handleRemoveFile}>Remove</Button>
                  </div>
                ) : (
                  <Dragger multiple={false} showUploadList={false} accept=".csv" beforeUpload={handleFileDropped} disabled={parsing}>
                    {parsing ? (
                      <Spin tip="Parsing…" />
                    ) : (
                      <>
                        <div style={{
                          width: 56, height: 56, borderRadius: "50%", background: THEME_PRIMARY_TINT, color: THEME_PRIMARY,
                          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, margin: "0 auto 12px",
                        }}
                        >
                          <InboxOutlined />
                        </div>
                        <p className="ant-upload-text">
                          Drop the Completed Orders sheet export (CSV) here, or click to browse
                        </p>
                        <p className="ant-upload-hint">
                          Nothing is rejected at this stage — the next step checks every row against live
                          reference data, marks the exact cells at fault, and lets you fix them inline.
                        </p>
                      </>
                    )}
                  </Dragger>
                )}

                <div style={{ textAlign: "center", marginTop: 16 }}>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Don't have the sheet yet?{" "}
                    <Button type="link" size="small" style={{ padding: 0, height: "auto", fontSize: 13 }} onClick={() => setStep(STEP.INSTRUCTIONS)}>
                      Go back and copy the template
                    </Button>
                  </Text>
                </div>
              </div>

              <div style={{ textAlign: "right", marginTop: 16 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => setStep(STEP.INSTRUCTIONS)} style={{ marginRight: 12 }}>
                  Back to template
                </Button>
                <Button type="primary" disabled={rows.length === 0} onClick={() => goToStep(STEP.REVIEW)}>
                  Next <ArrowRightOutlined />
                </Button>
              </div>
            </>
          )}

          {step === STEP.REVIEW && (
            <>
              <BulkFileDrop
                rows={rows}
                summary={fileMatch}
                pdfFiles={uploadedPdfs}
                onAttach={handleAttachFiles}
                onDetachAll={handleDetachAll}
                onAssignFile={handleAssignFile}
                onDetachFile={handleDetachFile}
                onRemovePdfFile={handleRemovePdfFile}
                onClearAllPdfFiles={handleClearAllPdfFiles}
              />

              <ReviewStep
                rows={rows}
                onChangeRows={handleChangeRows}
                masterData={masterData}
                pdfFiles={uploadedPdfs}
                errorsByCell={errorsByCell}
                dirtyCells={dirtyCells}
                rowStatus={rowStatus}
                focusedCell={focusedCell}
              />

              <ValidationPanel
                validation={validation}
                dirtyCount={dirtyCells.size}
                validating={validating}
                onRevalidate={() => runValidation(rows)}
                rowIndexById={rowIndexById}
                onFocusCell={handleFocusCell}
              />

              <div style={{ marginTop: 20, maxWidth: 380 }}>
                <Text strong>
                  Notification Email <span style={{ color: "#ff4d4f" }}>*</span>
                </Text>
                <Input
                  placeholder="Enter email address to receive status updates"
                  value={email}
                  status={email && !isEmailValid ? "error" : undefined}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ marginTop: 6 }}
                />
                {email && !isEmailValid && (
                  <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
                    Please enter a valid email address (e.g. name@company.com)
                  </Text>
                )}
                {!email && (
                  <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
                    Required before submitting — you'll be emailed when order creation finishes.
                  </Text>
                )}
              </div>

              <div style={{ flex: 1 }} />

              <div style={{ textAlign: "right", marginTop: 16 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => setStep(STEP.UPLOAD)} style={{ marginRight: 12 }}>Back</Button>
                <Button
                  icon={<DownloadOutlined />}
                  disabled={!validatedClean}
                  onClick={handleDownloadCorrected}
                  style={{ marginRight: 12 }}
                  title={!validatedClean ? "Fix every flagged row and re-validate first." : undefined}
                >
                  Download Corrected CSV
                </Button>
                <Button type="primary" disabled={!canSubmit} onClick={handleSubmit}>
                  {submitLabel()}
                </Button>
              </div>
            </>
          )}

          {step === STEP.RESULT && (
            <>
              {submitting ? (
                <>
                  <Text>Creating orders — {progress.done} / {progress.total} done. You'll be notified at {email} when this finishes.</Text>
                  <Progress percent={progress.total ? Math.round((progress.done / progress.total) * 100) : 0} status="active" style={{ marginTop: 12 }} />
                </>
              ) : (
                <>
                  {(() => {
                    const failedCount = results.filter((r) => !r.ok).length;
                    const okCount = results.length - failedCount;
                    return (
                      <Alert
                        type={failedCount ? "warning" : "success"}
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={failedCount
                          ? `${okCount} / ${results.length} orders created — ${failedCount} failed`
                          : `All ${results.length} order${results.length === 1 ? "" : "s"} created successfully`}
                      />
                    );
                  })()}
                  <Table size="small" rowKey="rowId" dataSource={results} columns={resultColumns} pagination={false} />
                  <div style={{ flex: 1 }} />
                  <div style={{ textAlign: "right", marginTop: 16 }}>
                    <Button type="primary" onClick={closeAndReset}>Close</Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        )}
      </Modal>
    </>
  );
}
